import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { RequestService } from "services/RequestService";

// Import the MultipleSelectChip component
import MultipleSelect from "../../schemas/multipleSelect"; // Adjust the import path as necessary

function AddForm({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: "",
    capacity: "",
    adminIds: [], // Add a new field for selected names
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSelectChange = (adminIds) => {
    setFormData({
      ...formData,
      adminIds,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "اسم المحطة مطلوب";
    }
    if (!formData.capacity) {
      newErrors.capacity = "السعة مطلوبة";
    } else if (isNaN(formData.capacity) || Number(formData.capacity) <= 0) {
      newErrors.capacity = "يجب أن تكون السعة رقماً موجباً";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await RequestService.post("PowerStation", {
        name: formData.name,
        capacity: Number(formData.capacity),
        adminIds: formData.adminIds, // Include selected names in the request
      });

      if (response.data && !response.data.error) {
        onSuccess(response);
        handleClose();
      } else {
        setErrors({
          submit: response.data?.message || "حدث خطأ أثناء إضافة المحطة",
        });
      }
    } catch (error) {
      console.error("Error adding power station:", error);
      setErrors({
        submit: error.response?.data?.message || "حدث خطأ أثناء إضافة المحطة",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      capacity: "",
      adminIds: [], // Reset selected names
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle>
        <MDTypography variant="h6">إضافة محطة جديدة</MDTypography>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <MDBox pt={2} pb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  name="name"
                  label="اسم المحطة"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  type="number"
                  name="capacity"
                  label="السعة"
                  value={formData.capacity}
                  onChange={handleChange}
                  error={!!errors.capacity}
                  helperText={errors.capacity}
                />
              </Grid>
              <Grid item xs={12}>
                <MultipleSelect
                  adminIds={formData.adminIds}
                  onChange={handleSelectChange}
                />
              </Grid>
              {errors.submit && (
                <Grid item xs={12}>
                  <MDTypography color="error" variant="caption">
                    {errors.submit}
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{
          padding: 2,
          display: 'flex',
          justifyContent: 'space-between',
          '& > button': {
            marginLeft: '8px',
            marginRight: '0px',
          }
        }}>
          <MDButton type="submit" color="info" disabled={isSubmitting}>
            {isSubmitting ? "جاري الإضافة..." : "إضافة"}
          </MDButton>
          <MDButton onClick={handleClose} color="secondary">
            إلغاء
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

AddForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddForm;